import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchUnitAction } from '../../features/UnitSlice/UnitSlice'
import { motion, AnimatePresence } from 'framer-motion'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import { SeeMore } from '../../Buttons/Buttons'
import { useTranslation } from 'react-i18next'
import Pagination from '../../Pagination/Pagination'
import Loading from '../Loading'

export default function UnitList() {
    const dispatch = useDispatch()
    const { loading, error, units, totalPages } = useSelector(state => state.fetchUnit)
    const [page, setPage] = useState(1)
    const perPage = 10
    const { t, i18n } = useTranslation();
    
    useEffect(() => {
        dispatch(fetchUnitAction({ page, perPage }))
    }, [dispatch, page, i18n.language])

    const prevPage = () => setPage(p => Math.max(1, p - 1))
    const nextPage = () => setPage(p => p + 1)

    const getExperienceYears = (date) => {
        const getDate = new Date(date)
        return new Date().getFullYear() - getDate.getFullYear()
    }
    
    const pagesCount = Number.isFinite(totalPages)
        ? Math.ceil(totalPages / perPage)
        : 0
        
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-8xl mx-auto">
              

                {loading && <Loading />}
                
                {error && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mb-8 p-6 bg-red-50 rounded-2xl border border-red-200 shadow-sm"
                    >
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-red-800 font-medium">{t('error') || 'Error'}</h3>
                                <p className="text-red-600 mt-1">{error.message}</p>
                            </div>
                        </div>
                    </motion.div>
                )}

                <AnimatePresence mode='wait'>
                    <motion.div
                        key={page}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="grid grid-cols-1 xl:grid-cols-2 gap-8"
                    >
                        {units?.length > 0 && units?.map((unit, index) => (
                            <motion.div
                                key={unit.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                                className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
                            >
                                {/* Unit Header */}
                                <div className="relative bg-gradient-to-r from-green-600 via-green-500 to-emerald-600 p-8">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-white/20 to-transparent"></div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 pr-16">
                                        {unit.title}
                                    </h2>
                                    
                                    <div className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xl font-bold">{index + 1}</span>
                                    </div>
                                </div>

                                {/* Members Grid */}
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {unit.members?.slice(0, 4).map((member, memberIndex) => (
                                            <motion.div
                                                key={member.id}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.3, delay: memberIndex * 0.1 }}
                                                whileHover={{ scale: 1.02 }}
                                                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group"
                                            >
                                                <div className="relative overflow-hidden" style={{ aspectRatio: "4/3" }}>
                                                    <LazyLoadImage
                                                        src={process.env.REACT_APP_IMG_URL + member.image}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        alt={member.name}
                                                        effect="blur"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                                                    
                                                    {/* Experience Badge */}
                                                    <div className="absolute top-3 right-3">
                                                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-full shadow-lg">
                                                            <span className="font-bold text-sm whitespace-nowrap">
                                                                {getExperienceYears(member.date)} {t("YearExp")}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Name Overlay */}
                                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                                                        <h3 className="text-white font-bold text-lg truncate">
                                                            {member.name}
                                                        </h3>
                                                        {member.position && (
                                                            <p className="text-green-200 text-sm truncate">
                                                                {member.position}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    {/* See More Button */}
                                    {unit?.members?.length > 4 && (
                                        <motion.div 
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="flex justify-center mt-6 pt-4 border-t border-gray-100"
                                        >
                                            <SeeMore 
                                                link={"/unit/" + unit.id} 
                                                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-2.5 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                            />
                                        </motion.div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>

                {/* Empty State */}
                {!loading && units?.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-16"
                    >
                        <div className="max-w-md mx-auto">
                            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                {t('noUnits') || 'No units found'}
                            </h3>
                            <p className="text-gray-600">
                                {t('noUnitsDescription') || 'There are currently no units available.'}
                            </p>
                        </div>
                    </motion.div>
                )}
                
                {/* Pagination */}
                {pagesCount > 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-12"
                    >
                        <Pagination
                            nextPage={nextPage}
                            setPage={setPage}
                            showPage={pagesCount}
                            page={page}
                            prevPage={prevPage}
                        />
                    </motion.div>
                )}
            </div>
        </div>
    )
}